import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { cancelOrder } from '../../api/trading'
import { ApiError } from '../../api/http'
import { usePortfolioSummary } from '../../hooks/usePortfolioSummary'
import { usePrefetchTicker } from '../../hooks/usePrefetchTicker'
import { useSetting } from '../../hooks/useSetting'
import { useMaskMoney } from '../../privacy/usePrivacyStore'
import { toast } from '../../toast/useToastStore'
import type { Order } from '../../api/types'
import { TradeLink } from '../TradeLink'

const ARM_TIMEOUT_MS = 3000
const OCC_RE = /^([A-Z]+)\d{6}[CP]\d{8}$/

function underlyingOf(symbol: string): string {
  const m = symbol.match(OCC_RE)
  return m ? m[1] : symbol
}

function rawFmtMoney(v: string | undefined): string {
  if (!v) return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return v
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message}: ${err.body || 'no detail'}`
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export function PendingOrdersRail() {
  const { pendingOrders } = usePortfolioSummary()
  const navigate = useNavigate()
  const prefetchTicker = usePrefetchTicker()
  const qc = useQueryClient()
  const mask = useMaskMoney()
  const fmtMoney = (v: string | undefined) => mask(rawFmtMoney(v))

  const [quickEnabled] = useSetting<boolean>('quick-trade-enabled', true)

  const [armedOrderId, setArmedOrderId] = useState<string | null>(null)
  const armOrderTimerRef = useRef<number | null>(null)
  const [submittingOrderId, setSubmittingOrderId] = useState<string | null>(null)

  const [armedCancelAll, setArmedCancelAll] = useState(false)
  const armCancelAllTimerRef = useRef<number | null>(null)
  const [cancellingAll, setCancellingAll] = useState(false)

  function clearArmOrder() {
    if (armOrderTimerRef.current != null) {
      window.clearTimeout(armOrderTimerRef.current)
      armOrderTimerRef.current = null
    }
    setArmedOrderId(null)
  }

  function clearArmCancelAll() {
    if (armCancelAllTimerRef.current != null) {
      window.clearTimeout(armCancelAllTimerRef.current)
      armCancelAllTimerRef.current = null
    }
    setArmedCancelAll(false)
  }

  useEffect(
    () => () => {
      clearArmOrder()
      clearArmCancelAll()
    },
    [],
  )

  const cancelMutation = useMutation({
    mutationFn: (orderId: string) => cancelOrder(orderId),
    onMutate: (orderId) => setSubmittingOrderId(orderId),
    onSuccess: (_resp, orderId) => {
      setSubmittingOrderId(null)
      toast.success(`Order ${orderId} · Cancel submitted`)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
    },
    onError: (err, orderId) => {
      setSubmittingOrderId(null)
      toast.error(`Cancel ${orderId} failed — ${errMessage(err)}`)
    },
  })

  function submitCancel(orderId: string) {
    clearArmOrder()
    cancelMutation.mutate(orderId)
  }

  function handleCancelClick(orderId: string) {
    if (submittingOrderId) return
    if (quickEnabled || armedOrderId === orderId) {
      submitCancel(orderId)
      return
    }
    setArmedOrderId(orderId)
    if (armOrderTimerRef.current != null) window.clearTimeout(armOrderTimerRef.current)
    armOrderTimerRef.current = window.setTimeout(() => {
      setArmedOrderId(null)
      armOrderTimerRef.current = null
    }, ARM_TIMEOUT_MS)
  }

  async function cancelAllPending(orderIds: string[]) {
    setCancellingAll(true)
    const results = await Promise.allSettled(orderIds.map((id) => cancelOrder(id)))
    setCancellingAll(false)
    const failures = results.filter((r) => r.status === 'rejected').length
    const successes = results.length - failures
    if (successes > 0) toast.success(`Cancel submitted for ${successes} order${successes === 1 ? '' : 's'}`)
    if (failures > 0) toast.error(`${failures} cancel${failures === 1 ? '' : 's'} failed`)
    qc.invalidateQueries({ queryKey: ['portfolio'] })
  }

  function handleCancelAll() {
    if (cancellingAll) return
    const ids = pendingOrders.map((o) => o.orderId)
    if (ids.length === 0) return
    if (quickEnabled || armedCancelAll) {
      clearArmCancelAll()
      void cancelAllPending(ids)
      return
    }
    setArmedCancelAll(true)
    if (armCancelAllTimerRef.current != null) window.clearTimeout(armCancelAllTimerRef.current)
    armCancelAllTimerRef.current = window.setTimeout(() => {
      setArmedCancelAll(false)
      armCancelAllTimerRef.current = null
    }, ARM_TIMEOUT_MS)
  }

  if (pendingOrders.length === 0) return null

  return (
    <div className="mt-auto flex flex-col min-h-0 border-t border-zinc-800 bg-white/[0.02]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
          Pending · {pendingOrders.length}
        </div>
        <button
          type="button"
          onClick={handleCancelAll}
          disabled={cancellingAll}
          className={`text-[10px] font-medium px-2 py-1 rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${
            armedCancelAll
              ? 'bg-amber-500 text-white ring-1 ring-amber-300 animate-pulse'
              : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'
          }`}
          title={armedCancelAll ? 'Click again to confirm' : 'Cancel all pending orders'}
        >
          {cancellingAll ? 'Cancelling…' : armedCancelAll ? 'Confirm' : 'Cancel all'}
        </button>
      </div>
      <ul className="flex-1 overflow-y-auto scrollbar-hide max-h-64">
        {pendingOrders.map((o: Order) => {
          const isArmed = armedOrderId === o.orderId
          const isSubmitting = submittingOrderId === o.orderId
          const anySubmitting = submittingOrderId !== null
          const navSym =
            o.instrument.type === 'OPTION' ? underlyingOf(o.instrument.symbol) : o.instrument.symbol
          const priceLabel =
            o.type === 'LIMIT'
              ? fmtMoney(o.limitPrice)
              : o.type === 'STOP'
                ? fmtMoney(o.stopPrice)
                : o.type === 'STOP_LIMIT'
                  ? `${fmtMoney(o.stopPrice)}/${fmtMoney(o.limitPrice)}`
                  : 'MKT'
          return (
            <li
              key={o.orderId}
              onClick={() => navigate(`/instruments/${navSym}`)}
              onMouseEnter={() => prefetchTicker(navSym)}
              className="px-3 py-2 border-b border-zinc-800/60 last:border-b-0 cursor-pointer hover:bg-white/[0.03]"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-zinc-100 truncate">
                      {o.instrument.symbol}
                    </span>
                    <span
                      className={`text-[10px] font-medium ${
                        o.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {o.side}
                    </span>
                    <TradeLink upstreamOrderId={o.orderId} variant="icon" stopPropagation />
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">
                    {o.type} · {o.quantity ?? '—'} @ {priceLabel}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCancelClick(o.orderId)
                  }}
                  disabled={anySubmitting}
                  title={isArmed ? 'Click again to confirm' : 'Cancel order'}
                  className={`flex-shrink-0 text-[10px] font-medium px-2 py-1 rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    isArmed
                      ? 'bg-amber-500 text-white ring-1 ring-amber-300 animate-pulse'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'
                  }`}
                >
                  {isSubmitting ? '…' : isArmed ? 'Confirm' : 'Cancel'}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
